//#region IMPORTS
import {
  isAnyOf,
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from "@reduxjs/toolkit";
import {
  InstoreOrder,
  InstoreOrderLine,
  InstoreOrderState,
  InstoreOrderParams,
  InstoreFlowCounts,
  FindTemplateRequest,
} from "../../models/instoreOrder";
import { InstoreTemplate } from "../../models/instoreTemplate";
import { toBackendDate } from "../../helpers/toBackendDate";
import { FieldValues } from "react-hook-form";
import { RootState } from "../../store/configureStore";
import { toast } from "react-toastify";
import getAxiosParams from "../../helpers/getAxiosParams";
import agent from "../../api/agent";
//#endregion

//#region INITIAL STATE
function initParams(): InstoreOrderParams {
  return {
    page: 1,
    limit: 25,
    sort: JSON.stringify({ column: "order_date", order: "desc" }),
  };
}

const initialExtraState: InstoreOrderState = {
  instoreOrderLinesLoaded: false,
  instoreOrdersLoaded: false,
  instoreOrder_params: initParams(),
  lastLoadedOrderId: null,
  metaData: undefined,
  loading: false,
  changed: false,
  status: "iddle",
  instoreOrders_pending: 0,
  instoreOrders_ordered: 0,
  instoreOrders_inProgress: 0,
  instoreFlowCounts: {
    pending: { week: 0, opening: 0, relocation: 0 },
    ordered: { week: 0, opening: 0, relocation: 0 },
    inProgress: { week: 0, opening: 0, relocation: 0 },
  },
  selectedInstoreOrders: [],
};
//#endregion

//#region CREATE ADAPTERS
const instoreOrdersAdapter = createEntityAdapter<InstoreOrder>();
const instoreOrderLinesAdapter = createEntityAdapter<InstoreOrderLine>();
//#endregion

//#region READ
// Fetch All
export const fetchInstoreOrdersAsync = createAsyncThunk<
  InstoreOrder[],
  void,
  { state: RootState }
>("instoreOrders/fetchInstoreOrdersAsync", async (_, thunkAPI) => {
  const state = thunkAPI.getState();
  const instoreOrderParams = getAxiosParams(
    state.instoreOrders.instoreOrder_params,
  );

  // Scope shop users to their own store. Backend is not (yet) scoping this
  const { user } = state.account;
  const isPrivileged =
    !!user &&
    (user.roles.includes("Admin") ||
      user.roles.includes("HQteam") ||
      user.roles.includes("Picker"));
  if (user && !isPrivileged && user.store?.external_store_id) {
    instoreOrderParams.set("external_store_id", user.store.external_store_id);
  }

  try {
    const { data, meta } = await agent.InstoreOrders.list(instoreOrderParams);
    thunkAPI.dispatch(setInstoreOrderMetaData(meta));
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

// Fetch by id
export const fetchInstoreOrderAsync = createAsyncThunk<
  InstoreOrder,
  number,
  { state: RootState }
>("instoreOrders/fetchInstoreOrderAsync", async (instoreOrder_id, thunkAPI) => {
  try {
    const { data } = await agent.InstoreOrders.detail(instoreOrder_id);
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

// Fetch pending count (for HQ Approve pill / sidebar badge)
export const fetchApprovableInstoreOrdersAsync = createAsyncThunk<
  number,
  void,
  { state: RootState }
>("instoreOrders/fetchApprovableInstoreOrdersAsync", async (_, thunkAPI) => {
  const params = new URLSearchParams();
  params.append("order_status", "Pending");
  params.append("limit", "1");
  try {
    const { meta } = await agent.InstoreOrders.list(params);
    thunkAPI.dispatch(setInstoreOrdersPending(meta.total));
    return meta.total;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.message ?? "fetch failed");
  }
});

// Fetch ordered count (for picker/admin Manage pill / sidebar badge)
export const fetchShippableInstoreOrdersAsync = createAsyncThunk<
  number,
  void,
  { state: RootState }
>("instoreOrders/fetchShippableInstoreOrdersAsync", async (_, thunkAPI) => {
  const params = new URLSearchParams();
  params.append("order_status", "Ordered");
  params.append("limit", "1");
  try {
    const { meta } = await agent.InstoreOrders.list(params);
    thunkAPI.dispatch(setInstoreOrdersOrdered(meta.total));
    return meta.total;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.message ?? "fetch failed");
  }
});

// Fetch in-progress count (for picker/admin Send pill / sidebar + tab badge)
export const fetchInProgressInstoreOrdersAsync = createAsyncThunk<
  number,
  void,
  { state: RootState }
>("instoreOrders/fetchInProgressInstoreOrdersAsync", async (_, thunkAPI) => {
  const params = new URLSearchParams();
  params.append("order_status", "In progress");
  params.append("limit", "1");
  try {
    const { meta } = await agent.InstoreOrders.list(params);
    thunkAPI.dispatch(setInstoreOrdersInProgress(meta.total));
    return meta.total;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.message ?? "fetch failed");
  }
});

// Fetch the flow-pill counts split per order-type bucket (Week vs
// Opening/Relocation) for each workflow status. Picker/Admin only — feeds the
// split bubbles + button-vs-dropdown decision in InstorePresetPills. Fires 6
// light limit=1 list calls (3 statuses × 2 buckets). Backend treats the comma in
// "Opening,Relocation" as an OR filter (same as the overview preset).
export const fetchInstoreFlowCountsAsync = createAsyncThunk<
  InstoreFlowCounts,
  void,
  { state: RootState }
>("instoreOrders/fetchInstoreFlowCountsAsync", async (_, thunkAPI) => {
  const statuses = [
    ["pending", "Pending"],
    ["ordered", "Ordered"],
    ["inProgress", "In progress"],
  ] as const;
  // Separate calls per type — the backend cannot (yet) filter on a combined
  // "Opening,Relocation" value, so Opening and Relocation are queried apart.
  const buckets = [
    ["week", "Week"],
    ["opening", "Opening"],
    ["relocation", "Relocation"],
  ] as const;

  const countFor = async (statusValue: string, typeName: string) => {
    const params = new URLSearchParams();
    params.append("order_status", statusValue);
    params.append("order_type_instore_name", typeName);
    params.append("limit", "1");
    const { meta } = await agent.InstoreOrders.list(params);
    return meta.total as number;
  };

  try {
    const result: InstoreFlowCounts = {
      pending: { week: 0, opening: 0, relocation: 0 },
      ordered: { week: 0, opening: 0, relocation: 0 },
      inProgress: { week: 0, opening: 0, relocation: 0 },
    };
    await Promise.all(
      statuses.flatMap(([statusKey, statusValue]) =>
        buckets.map(async ([bucketKey, typeName]) => {
          result[statusKey][bucketKey] = await countFor(statusValue, typeName);
        }),
      ),
    );
    thunkAPI.dispatch(setInstoreFlowCounts(result));
    return result;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error?.message ?? "fetch failed");
  }
});

// Fetch template by order params
export const fetchMatchingInstoreTemplateAsync = createAsyncThunk<
  { template: InstoreTemplate; instore_order_lines: InstoreOrderLine[] },
  FindTemplateRequest,
  { state: RootState }
>(
  "instoreTemplates/fetchMatchingInstoreTemplateAsync",
  async (values, thunkAPI) => {
    try {
      const response = await agent.InstoreOrders.fitTemplate({ ...values });
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  },
);
//#endregion

//#region CREATE
export const createInstoreOrderAsync = createAsyncThunk<
  InstoreOrder,
  { values: FieldValues | null },
  { state: RootState }
>("instoreOrders/createInstoreOrderAsync", async ({ values }, thunkAPI) => {
  try {
    const { data } = await agent.InstoreOrders.create({ ...values });
    thunkAPI.dispatch(fetchApprovableInstoreOrdersAsync());
    thunkAPI.dispatch(fetchShippableInstoreOrdersAsync());
    thunkAPI.dispatch(fetchInProgressInstoreOrdersAsync());
    thunkAPI.dispatch(fetchInstoreFlowCountsAsync());
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
//#endregion

//#region UPDATE
export const updateInstoreOrderAsync = createAsyncThunk<
  { data: InstoreOrder; shouldNotify?: boolean },
  { id: number; values: FieldValues; shouldNotify?: boolean },
  { state: RootState }
>(
  "instoreOrders/updateInstoreOrderAsync",
  async ({ id, values, shouldNotify = true }, thunkAPI) => {
    try {
      const { data } = await agent.InstoreOrders.update(id, { ...values });
      thunkAPI.dispatch(fetchApprovableInstoreOrdersAsync());
      thunkAPI.dispatch(fetchShippableInstoreOrdersAsync());
      thunkAPI.dispatch(fetchInProgressInstoreOrdersAsync());
      thunkAPI.dispatch(fetchInstoreFlowCountsAsync());
      return { data, shouldNotify };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  },
);

// Bulk status update: client-side loop over selected IDs. The backend has no
// bulk endpoint for instore orders, so we fan out PUT /instore-orders/:id.
// `mode: "approve"` splits the batch by each order's `approved` flag: true →
// Ordered, false → Cancelled. `mode: "in-progress"` puts everything into In
// progress. `mode: "ship"` puts everything into Shipped.
export const bulkUpdateInstoreOrderStatusAsync = createAsyncThunk<
  { ids: number[]; failed: number[] },
  { ids: number[]; mode: "approve" | "in-progress" | "ship" },
  { state: RootState }
>(
  "instoreOrders/bulkUpdateInstoreOrderStatusAsync",
  async ({ ids, mode }, thunkAPI) => {
    const state = thunkAPI.getState();
    const allOrders = instoreOrdersSelectors.selectAll(state);
    const map = new Map(allOrders.map((o) => [o.id, o] as const));
    const failed: number[] = [];

    await Promise.all(
      ids.map(async (id) => {
        const order = map.get(id);
        if (!order) {
          failed.push(id);
          return;
        }
        const targetStatus =
          mode === "ship"
            ? "Shipped"
            : mode === "in-progress"
              ? "In progress"
              : order.approved === false
                ? "Cancelled"
                : "Ordered";
        try {
          await agent.InstoreOrders.update(id, {
            order_status: targetStatus,
            order_code: order.order_code,
            order_type_instore_id: order.order_type_instore_id,
            order_date: toBackendDate(order.order_date),
            instore_type_id: order.instore_type_id,
            template_id: order.template_id,
          });
        } catch {
          failed.push(id);
        }
      }),
    );

    if (failed.length > 0) {
      toast.error(
        `${failed.length} instore order${failed.length === 1 ? "" : "s"} failed to update`,
      );
    } else {
      toast.success(
        `${ids.length} instore order${ids.length === 1 ? "" : "s"} processed`,
      );
    }
    await thunkAPI.dispatch(fetchInstoreOrdersAsync());
    await thunkAPI.dispatch(fetchApprovableInstoreOrdersAsync());
    await thunkAPI.dispatch(fetchShippableInstoreOrdersAsync());
    await thunkAPI.dispatch(fetchInProgressInstoreOrdersAsync());
    await thunkAPI.dispatch(fetchInstoreFlowCountsAsync());
    return { ids, failed };
  },
);

export const updateManyInstoreOrderLinesAsync = createAsyncThunk<
  InstoreOrder,
  { instoreOrder_id: number; values: FieldValues }
>(
  "instoreOrders/updateManyInstoreOrderLinesAsync",
  async ({ instoreOrder_id, values }, thunkAPI) => {
    try {
      const { data } = await agent.InstoreOrderLines.updateMany(
        instoreOrder_id,
        { ...values },
      );
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState() as RootState;
      return !state.instoreOrders.changed;
    },
  },
);
//#endregion

//#region DELETE
export const deleteInstoreOrderAsync = createAsyncThunk<
  number,
  { id: number },
  { state: RootState }
>("instoreOrders/deleteInstoreOrderAsync", async ({ id }, thunkAPI) => {
  try {
    await agent.InstoreOrders.delete(id);
    thunkAPI.dispatch(fetchApprovableInstoreOrdersAsync());
    thunkAPI.dispatch(fetchShippableInstoreOrdersAsync());
    thunkAPI.dispatch(fetchInProgressInstoreOrdersAsync());
    thunkAPI.dispatch(fetchInstoreFlowCountsAsync());
    return id;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
//#endregion

const initialState = {
  orders: instoreOrdersAdapter.getInitialState(),
  orderLines: instoreOrderLinesAdapter.getInitialState(),
  ...initialExtraState,
};

//#region CREATE REDUCER SLICE
export const instoreOrderSlice = createSlice({
  name: "instoreOrders",
  initialState,
  reducers: {
    setInstoreOrderParams: (state, action) => {
      state.instoreOrdersLoaded = false;
      state.instoreOrder_params = {
        ...state.instoreOrder_params,
        ...action.payload,
        page: 1,
      };
    },
    upsertInstoreOrderLineRowLocal: (state, action) => {
      instoreOrderLinesAdapter.upsertOne(state.orderLines, action.payload);
      state.changed = true;
    },
    upsertManyInstoreOrderLineRowLocal: (state, action) => {
      instoreOrderLinesAdapter.upsertMany(state.orderLines, action.payload);
      state.changed = true;
    },
    removeInstoreOrderLineRowLocal: (state, action) => {
      instoreOrderLinesAdapter.removeOne(state.orderLines, action.payload);
      state.changed = true;
    },
    removeManyInstoreOrderLineRowLocal: (state, action) => {
      instoreOrderLinesAdapter.removeMany(state.orderLines, action.payload);
      state.changed = true;
    },
    clearInstoreOrderParams: (state) => {
      state.instoreOrdersLoaded = false;
      state.instoreOrder_params = initParams();
    },
    clearInstoreOrderParam: (state, action) => {
      const newParmams = { ...state.instoreOrder_params };
      delete newParmams[action.payload];
      state.instoreOrdersLoaded = false;
      state.instoreOrder_params = newParmams;
    },
    setInstoreOrderPageNumber: (state, action) => {
      state.instoreOrdersLoaded = false;
      state.instoreOrder_params = {
        ...state.instoreOrder_params,
        ...action.payload,
      };
    },
    setInstoreOrderLoading: (state, action) => {
      state.loading = action.payload;
    },
    setInstoreOrderMetaData: (state, action) => {
      state.metaData = action.payload;
    },
    setInstoreOrdersPending: (state, action) => {
      state.instoreOrders_pending = action.payload;
    },
    setInstoreOrdersOrdered: (state, action) => {
      state.instoreOrders_ordered = action.payload;
    },
    setInstoreOrdersInProgress: (state, action) => {
      state.instoreOrders_inProgress = action.payload;
    },
    setInstoreFlowCounts: (state, action) => {
      state.instoreFlowCounts = action.payload;
    },
    setSelectedInstoreOrders: (state, action) => {
      state.selectedInstoreOrders = action.payload;
    },
    toggleSelectedInstoreOrder: (state, action) => {
      const id: number = action.payload;
      state.selectedInstoreOrders = state.selectedInstoreOrders.includes(id)
        ? state.selectedInstoreOrders.filter((x) => x !== id)
        : [...state.selectedInstoreOrders, id];
    },
    updateApproveStateOfInstoreOrder: (state, action) => {
      const { id, approved } = action.payload;
      const existing = state.orders.entities[id];
      if (existing) {
        instoreOrdersAdapter.upsertOne(state.orders, {
          ...existing,
          approved,
        });
      }
    },
    resetInstoreOrderParams: (state) => {
      state.instoreOrdersLoaded = false;
      state.instoreOrder_params = initParams();
    },
    clearInstoreOrders: () => initialState,
  },
  extraReducers: (builder) => {
    // FETCH instoreOrders
    builder.addCase(fetchInstoreOrdersAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreOrders";
    });
    builder.addCase(fetchInstoreOrdersAsync.fulfilled, (state, { payload }) => {
      instoreOrdersAdapter.setAll(state.orders, payload);
      state.instoreOrdersLoaded = true;
      state.lastLoadedOrderId = null;
      state.loading = false;
      state.status = "idle";
    });

    // FETCH InstoreOrder by id and set orderlines
    builder.addCase(fetchInstoreOrderAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreOrder";
      state.instoreOrderLinesLoaded = false;
    });
    builder.addCase(fetchInstoreOrderAsync.fulfilled, (state, { payload }) => {
      instoreOrdersAdapter.upsertOne(state.orders, payload);
      instoreOrderLinesAdapter.setAll(
        state.orderLines,
        payload.instore_order_lines,
      );
      state.instoreOrderLinesLoaded = true;
      state.lastLoadedOrderId = payload.id;
      state.loading = false;
      state.changed = false;
      state.status = "idle";
    });

    // FETCH matching instoreTemplate by order params and set rows
    builder.addCase(fetchMatchingInstoreTemplateAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchMatchingInstoreTemplateAsync";
    });
    builder.addCase(
      fetchMatchingInstoreTemplateAsync.fulfilled,
      (state, { payload }) => {
        instoreOrderLinesAdapter.setAll(
          state.orderLines,
          payload.instore_order_lines.map((item, index) => ({
            ...item,
            id: index,
          })),
        );
        state.instoreOrderLinesLoaded = true;
        state.loading = false;
        state.changed = false;
        state.status = "idle";
      },
    );

    // CREATE InstoreOrder
    builder.addCase(createInstoreOrderAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingCreateInstoreOrder";
    });
    builder.addCase(createInstoreOrderAsync.fulfilled, (state, { payload }) => {
      instoreOrdersAdapter.addOne(state.orders, payload);
      instoreOrderLinesAdapter.setAll(
        state.orderLines,
        payload.instore_order_lines,
      );
      toast.success("Instore order created");
      state.metaData &&= {
        ...state.metaData,
        total: state.metaData.total + 1,
        to: state.metaData.to + 1,
      };
      state.loading = false;
      state.changed = false;
      state.status = "idle";
    });

    // UPDATE InstoreOrder
    builder.addCase(updateInstoreOrderAsync.pending, (state, { meta }) => {
      state.loading = true;
      state.status = "PendingUpdateInstoreOrder" + meta.arg.id;
    });
    builder.addCase(updateInstoreOrderAsync.fulfilled, (state, { payload }) => {
      instoreOrdersAdapter.upsertOne(state.orders, payload.data);
      payload.shouldNotify &&
        toast.success(`Instore order ${payload.data.order_code} updated`);
      state.changed = false;
      state.loading = false;
      // Force a list refetch so the changed order drops out of a status-locked
      // view (used by the flow auto-advance when the list empties).
      state.instoreOrdersLoaded = false;
      state.status = "idle";
    });

    // UPDATE MANY InstoreOrderLines
    builder.addCase(
      updateManyInstoreOrderLinesAsync.pending,
      (state, { meta }) => {
        state.loading = true;
        state.status =
          "PendingUpdateManyInstoreOrderLines" + meta.arg.instoreOrder_id;
      },
    );
    builder.addCase(
      updateManyInstoreOrderLinesAsync.fulfilled,
      (state, { payload }) => {
        instoreOrderLinesAdapter.upsertMany(
          state.orderLines,
          payload.instore_order_lines,
        );
        state.changed = false;
        state.loading = false;
        state.status = "idle";
      },
    );

    // DELETE InstoreOrder
    builder.addCase(deleteInstoreOrderAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingDeleteInstoreOrder";
    });
    builder.addCase(deleteInstoreOrderAsync.fulfilled, (state, { payload }) => {
      instoreOrdersAdapter.removeOne(state.orders, payload);
      state.orderLines = instoreOrderLinesAdapter.getInitialState();
      toast.success("Instore order deleted");
      state.lastLoadedOrderId = null;
      state.metaData &&= {
        ...state.metaData,
        total:
          state.metaData.total > 0
            ? state.metaData.total - 1
            : state.metaData.total,
        to: state.metaData.to > 0 ? state.metaData.to - 1 : state.metaData.to,
      };
      state.loading = false;
      state.changed = false;
      state.status = "idle";
    });

    //#region CRUD REJECTION
    builder.addMatcher(
      isAnyOf(
        fetchInstoreOrderAsync.rejected,
        fetchInstoreOrdersAsync.rejected,
        createInstoreOrderAsync.rejected,
        updateInstoreOrderAsync.rejected,
        deleteInstoreOrderAsync.rejected,
        updateManyInstoreOrderLinesAsync.rejected,
        fetchMatchingInstoreTemplateAsync.rejected,
      ),
      (state, { payload }) => {
        state.orderLines = instoreOrderLinesAdapter.getInitialState();
        toast.error(`${payload}`);
        state.lastLoadedOrderId = null;
        state.instoreOrderLinesLoaded = false;
        state.loading = false;
        state.status = "idle";
      },
    );
    //#endregion
  },
});
//#endregion

//#region NORMALISED STATE
export const instoreOrdersSelectors = instoreOrdersAdapter.getSelectors(
  (state: RootState) => state.instoreOrders.orders,
);

export const instoreOrderLinesSelectors = instoreOrderLinesAdapter.getSelectors(
  (state: RootState) => state.instoreOrders.orderLines,
);
//#endregion

//#region EXPORT
export const {
  removeManyInstoreOrderLineRowLocal,
  upsertManyInstoreOrderLineRowLocal,
  updateApproveStateOfInstoreOrder,
  removeInstoreOrderLineRowLocal,
  upsertInstoreOrderLineRowLocal,
  toggleSelectedInstoreOrder,
  setInstoreOrderPageNumber,
  setSelectedInstoreOrders,
  setInstoreOrderMetaData,
  setInstoreOrdersPending,
  setInstoreOrdersOrdered,
  setInstoreOrdersInProgress,
  setInstoreFlowCounts,
  resetInstoreOrderParams,
  clearInstoreOrderParams,
  clearInstoreOrderParam,
  setInstoreOrderLoading,
  setInstoreOrderParams,
  clearInstoreOrders,
} = instoreOrderSlice.actions;
//#endregion

export default instoreOrderSlice.reducer;
